import { useParams, useNavigate, generatePath } from 'react-router-dom'
import { useEffect, useState, useRef } from 'react'
import routes from '../../components/Router/routes'
import { UniversalPageLayout, SearchableSelect, LoadingSpinner, InlineAlert, Button } from '../../components/BaseComponents'
import PageHeadings from '../../components/BaseComponents/PageHeadings/PageHeadings'
import PageTemplate from '../../components/BaseComponents/PageTemplate/PageTemplate'
import useApi from '../../hooks/useApi'
import useCleanup from '../../hooks/useCleanup'
import { formatDate, formatScheduledToStart, getApiErrorMessage } from '../../lib/utils'
import { sortTrainingSessions } from '../../lib/sortHelpers'
import { useTrainingSessionDetailZones } from './useTrainingSessionDetailZones'
import AdminTrainingSessionUsers from './AdminTrainingSessionUsers'
import EditTrainingSessionModal from './EditTrainingSessionModal'
import DeleteTrainingSessionModal from './DeleteTrainingSessionModal'

function AdminTrainingSessionDetail() {
	const { trainingSessionId } = useParams()
	const navigate = useNavigate()
	const api = useApi()
	const usersRef = useRef<any>(null)

	const [sessions, setSessions] = useState<any[]>([])
	const [sessionData, setSessionData] = useState<any>(null)
	const [loading, setLoading] = useState(true)
	const [loadedSessionId, setLoadedSessionId] = useState<string>()
	const [loadedApi, setLoadedApi] = useState<ReturnType<typeof useApi>>()
	const [sessionError, setSessionError] = useState('')
	const [sessionsLoading, setSessionsLoading] = useState(true)
	const [sessionsError, setSessionsError] = useState('')
	const [editModalOpen, setEditModalOpen] = useState(false)
	const [deleteModalOpen, setDeleteModalOpen] = useState(false)

	// Filter state for users table
	const [userNameFilter, setUserNameFilter] = useState('')
	const [organisationNameFilter, setOrganisationNameFilter] = useState('')

	if (!trainingSessionId) {
		throw new Error('No training session Id')
	}

	// Load all sessions and current session data
	const loadSessions = useCleanup(() => {
		let cancelled = false
		setSessionsLoading(true)
		setSessionsError('')
		const run = async () => {
			try {
				const result = await api.get('admin/training-sessions?perPage=100')
				if (cancelled) return
				setSessions(result.data.trainingSessions || [])
			} catch (err) {
				if (!cancelled) setSessionsError(getApiErrorMessage(err, 'Could not load the session list. Please try again.'))
			} finally {
				if (!cancelled) setSessionsLoading(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	})

	const loadSessionData = useCleanup(() => {
		let cancelled = false
		setLoadedSessionId(trainingSessionId)
		setLoadedApi(api)
		setLoading(true)
		setSessionError('')
		setSessionData(null)
		const run = async () => {
			try {
				const result = await api.get(`admin/training-sessions/${trainingSessionId}`)
				if (cancelled) return
				if (!result.data) throw new Error('Missing training session data')
				setSessionData(result.data)
			} catch (err) {
				if (!cancelled) setSessionError(getApiErrorMessage(err, 'Could not load session details. Please try again.'))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}
		run()
		return () => {
			cancelled = true
		}
	})

	useEffect(() => {
		loadSessions()
		loadSessionData()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trainingSessionId, api])

	// Build session selector with sorting matching the admin table
	// Uses the same logic as the backend: sort by start_date DESC, but when
	// start_date is null (meaning "starts immediately"), use created_at instead.
	// This ensures null-date sessions are spliced into the list by creation date,
	// not clumped at the bottom.
	const sortedSessions = sortTrainingSessions(sessions, 'desc')

	const sessionOptions = sortedSessions.map((session) => ({
		value: session.id,
		label: session.start_date
			? `(${formatDate(session.start_date)}) ${session.name}`
			: `(${formatDate(session.created_at)})* ${session.name}`,
	}))

	const sessionSelector = sessionsError ? (
		<InlineAlert type='error'>
			<p>{sessionsError}</p>
			<Button type='Primary' text='Retry session list' onClick={loadSessions} />
		</InlineAlert>
	) : sessionsLoading ? (
		<LoadingSpinner inline loadingText='Loading sessions...' />
	) : (
		<SearchableSelect
			options={sessionOptions}
			value={sessionOptions.find((opt) => opt.value === trainingSessionId)}
			placeholder='Select training session'
			isSearchable={false}
			isClearable={false}
			onChange={(option) => {
				if (option) {
					navigate(generatePath(routes.adminTrainingSession.path, { trainingSessionId: option.value }))
				}
			}}
		/>
	)

	// Format start date for display - a null start_date means the session starts
	// immediately (Decision 11), so show the session's creation date instead.
	const formattedStartDate = formatScheduledToStart(sessionData?.start_date, sessionData?.created_at)

	// Count users
	const userCount = Number(sessionData?.users_count) || 0
	const pendingCount = Number(sessionData?.pending_count) || 0

	// Call hook unconditionally - BEFORE any conditional returns
	const zones = useTrainingSessionDetailZones({
		sessionSelector,
		sessionName: sessionData?.name ?? '',
		sessionStartDate: formattedStartDate,
		jurisdictionCode: sessionData?.jurisdiction_code,
		userCount,
		pendingCount,
		userNameFilterValue: userNameFilter,
		userNameFilterPlaceholder: 'Search by user name',
		onUserNameFilterChange: setUserNameFilter,
		organisationNameFilterValue: organisationNameFilter,
		organisationNameFilterPlaceholder: 'Search by organisation name',
		onOrganisationNameFilterChange: setOrganisationNameFilter,
		onEditSessionClick: () => setEditModalOpen(true),
		onDeleteSessionClick: () => setDeleteModalOpen(true),
		onUploadUsersClick: () => usersRef.current?.openUploadModal(),
		onAddTrainingUserClick: () => usersRef.current?.openAddModal(),
		onManageOrganisationsClick: () => usersRef.current?.openManageOrganisationsModal(),
	})

	// NOW do conditional returns after hooks are called
	if (loading || loadedSessionId !== trainingSessionId || loadedApi !== api) {
		return <LoadingSpinner />
	}

	if (!sessionData) {
		return (
			<PageTemplate>
				<PageHeadings crumbs={[
					{ label: routes.adminTrainingSession.menuName as string, route: routes.adminTrainingSessions.path },
					{ label: 'Training session' },
				]} />
				<InlineAlert type='error'>
					<p>{sessionError || 'Could not load session details. Please try again.'}</p>
					<Button type='Primary' text='Retry session details' onClick={loadSessionData} />
				</InlineAlert>
			</PageTemplate>
		)
	}

	return (
		<UniversalPageLayout
			documentTitle={routes.adminTrainingSession.name}
			testid='admin-training-session-detail'
			heading={{
				crumbs: [
					{ label: routes.adminTrainingSession.menuName as string, route: routes.adminTrainingSessions.path },
					{ label: sessionData.name },
				],
			}}
			primarySelector={zones.primarySelector}
			summary={zones.summary}
			filtersSimple={zones.filtersAndActions}
		>
			<AdminTrainingSessionUsers
				ref={usersRef}
				trainingSessionId={trainingSessionId}
				sessionData={sessionData}
				userNameFilter={userNameFilter}
				organisationNameFilter={organisationNameFilter}
				onDataRefresh={() => {
					loadSessionData()
				}}
			/>
			<EditTrainingSessionModal
				isOpen={editModalOpen}
				onClose={() => setEditModalOpen(false)}
				onEdited={() => {
					setEditModalOpen(false)
					loadSessionData()
					loadSessions()
				}}
				sessionId={trainingSessionId}
				sessionName={sessionData.name || ''}
				sessionStartDate={sessionData.start_date || null}
				sessionJurisdictionCode={sessionData.jurisdiction_code}
			/>
			<DeleteTrainingSessionModal
				isOpen={deleteModalOpen}
				onClose={() => setDeleteModalOpen(false)}
				onDeleted={() => {
					setDeleteModalOpen(false)
					navigate(routes.adminTrainingSessions.path)
				}}
				sessionId={trainingSessionId}
				sessionName={sessionData.name || ''}
			/>
		</UniversalPageLayout>
	)
}

export default AdminTrainingSessionDetail
